import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Container,
  Fade,
  Grow,
  Zoom
} from '@mui/material';
import {
  School,
  CalendarToday,
  Person,
  Phone,
  Email,
  Home,
  TrendingUp,
  CheckCircle,
  Cancel,
  Weekend,
  EventBusy
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_BASE_URL || "https://wsmsv1.aidigitalsec.com";

const ParentAttendance = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [studentProfile, setStudentProfile] = useState(null);
  const [attendanceData, setAttendanceData] = useState([]);
  const [summary, setSummary] = useState({
    totalDays: 0,
    present: 0,
    absent: 0,
    holidays: 0,
    attendancePercent: 0
  });
  const [selectedYearId, setSelectedYearId] = useState(4);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [yearOptions, setYearOptions] = useState([]);

  useEffect(() => {
    const currentMonthIndex = new Date().getMonth();
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    setSelectedMonth(monthNames[currentMonthIndex]);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const studentId = sessionStorage.getItem("employeeId");
        const token = sessionStorage.getItem("token");

        if (!studentId) {
          throw new Error("Student ID not found");
        }

        const requestData = {
          studentId: studentId,
          yearId: selectedYearId.toString()
        };

        const response = await fetch(
          `${API_URL}/Attendance/StudentProfile`,
          {
            method: 'POST',
            headers: {
              "Content-Type": "application/json",
              "Accept": "application/json",
              "Authorization": token,
            },
            body: JSON.stringify(requestData)
          }
        );

        const data = await response.json();

        if (data) {
          setStudentProfile(data.objData);
          if (data.objData?.rollNo) {
            sessionStorage.setItem("rollNo", data.objData.rollNo);
          }
          setAttendanceData(data.objattendancelist || []);

          if (data?.totalAttendancList?.length > 0) {
            const attendanceData = data.totalAttendancList[0];
            setSummary({
              totalDays: attendanceData.totalDays ?? 0,
              present: attendanceData.presentCount ?? 0,
              absent: attendanceData.absentCount ?? 0,
              holidays: attendanceData.holidayCount ?? 0,
              attendancePercent: attendanceData.attendancePercentage ?? 0
            });
          }
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedYearId]);

  useEffect(() => {
    const monthData = attendanceData.find(m => m.month === selectedMonth);

    if (monthData) {
      let presentCount = 0;
      let absentCount = 0;
      let holidayCount = 0;

      const romanKeys = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x',
        'xi', 'xii', 'xiii', 'xiv', 'xv', 'xvi', 'xvii', 'xviii', 'xix', 'xx',
        'xxi', 'xxii', 'xxiii', 'xxiv', 'xxv', 'xxvi', 'xxvii', 'xxviii', 'xxix', 'xxx', 'xxxi'];

      romanKeys.forEach(key => {
        const status = monthData[key];
        if (status === 'P') presentCount++;
        else if (status === 'A') absentCount++;
        else if (status === 'H') holidayCount++;
      });

      const totalDays = presentCount + absentCount;
      const attendancePercent = totalDays > 0 ? ((presentCount / totalDays) * 100).toFixed(2) : 0;

      setSummary({
        totalDays,
        present: presentCount,
        absent: absentCount,
        holidays: holidayCount,
        attendancePercent
      });
    }
  }, [selectedMonth, attendanceData]);

  useEffect(() => {
    const fetchFinancialYears = async () => {
      try {
        const token = sessionStorage.getItem("token");
        const response = await fetch(`${API_URL}/ClassPromotion/GetFinancialYear`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
          body: JSON.stringify({}),
        });

        if (!response.ok) throw new Error("Failed to fetch financial years");
        const data = await response.json();
        setYearOptions(data);

        const today = new Date();
        const currentYear = today.getFullYear();
        const nextYear = currentYear + 1;
        const financialYearStart = new Date(currentYear, 3, 1);
        const financialYearEnd = new Date(nextYear, 2, 31);

        let currentSessionString;
        if (today >= financialYearStart && today <= financialYearEnd) {
          currentSessionString = `${currentYear}-${nextYear}`;
        } else {
          currentSessionString = `${currentYear - 1}-${currentYear}`;
        }

        const currentSession = data.find(
          (item) => item.finanacialYear === currentSessionString
        );
        if (currentSession) {
          setSelectedYearId(currentSession.financialYearID);
        }
      } catch (error) {
        console.error("Error fetching years:", error);
      }
    };

    fetchFinancialYears();
  }, []);

  const getDayStatus = (month, day) => {
    const monthData = attendanceData.find(m => m.month === month);
    if (!monthData) return null;

    const romanKeys = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x',
      'xi', 'xii', 'xiii', 'xiv', 'xv', 'xvi', 'xvii', 'xviii', 'xix', 'xx',
      'xxi', 'xxii', 'xxiii', 'xxiv', 'xxv', 'xxvi', 'xxvii', 'xxviii', 'xxix', 'xxx', 'xxxi'];

    const key = romanKeys[day - 1];
    return monthData[key] || '';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'P': return '#e8f5e9';
      case 'A': return '#ffebee';
      case 'H': return '#e3f2fd';
      case 'W': return '#fff9c4';
      default: return '#ffffff';
    }
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        // background: 'linear-gradient(135deg, rgba(75,108,183,0.05) 0%, rgba(24,40,72,0.05) 100%)'
      }}>
        <CircularProgress
          size={60}
          sx={{
            color: 'linear-gradient(110deg, rgba(75,108,183,1) 11.3%, rgba(24,40,72,1) 99.3%)',
            animation: 'spin 1.5s linear infinite',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' }
            }
          }}
        />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ animation: 'shake 0.5s ease-in-out', '@keyframes shake': {
          '0%, 100%': { transform: 'translateX(0)' },
          '25%': { transform: 'translateX(-5px)' },
          '75%': { transform: 'translateX(5px)' }
        }}}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      pb: 6,
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '400px',
        // background: 'linear-gradient(135deg, rgba(75,108,183,0.05) 0%, rgba(24,40,72,0.05) 100%)',
        zIndex: 0,
        animation: 'gradientShift 10s ease infinite'
      },
      '@keyframes gradientShift': {
        '0%, 100%': { opacity: 1 },
        '50%': { opacity: 0.6 }
      }
    }}>
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Fade in={true} timeout={1000}>
          <Box sx={{ pt: 2, pb: 3 }}>
            <Typography
              variant="h4"
              sx={{
                color: 'rgba(49, 70, 119, 1)',
                fontWeight: 700,
                textAlign: 'center',
                mb: 1,
                animation: 'slideDown 0.8s ease-out',
                '@keyframes slideDown': {
                  '0%': { transform: 'translateY(-30px)', opacity: 0 },
                  '100%': { transform: 'translateY(0)', opacity: 1 }
                }
              }}
            >
              Parents Dashboard
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(75,108,183,1)',
                textAlign: 'center',
                animation: 'fadeIn 1s ease-out 0.3s both',
                '@keyframes fadeIn': {
                  '0%': { opacity: 0 },
                  '100%': { opacity: 1 }
                }
              }}
            >
              Welcome back! Here's your child's academic overview
            </Typography>
          </Box>
        </Fade>

        {studentProfile && (
          <Grow in={true} timeout={1000}>
            <Card sx={{
              mb: 4,
              borderRadius: 4,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
              background: 'linear-gradient(110deg, rgba(75,108,183,1) 11.3%, rgba(24,40,72,1) 99.3%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-50%',
                right: '-50%',
                width: '200%',
                height: '200%',
                background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                animation: 'rotate 20s linear infinite'
              },
              '@keyframes rotate': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }}>
              <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
                <Grid container spacing={4} alignItems="center">
                  <Grid item xs={12} md={3} sx={{ textAlign: 'center' }}>
                    {(() => {
                      const imageUrl = studentProfile.studentImageUrl
                        ? studentProfile.studentImageUrl.startsWith('http')
                          ? studentProfile.studentImageUrl
                          : `https://wsmsv1.aidigitalsec.com/${studentProfile.studentImageUrl}`
                        : "";
                      return (
                        <>
                          <Zoom in={true} timeout={800}>
                            <Avatar
                              src={imageUrl}
                              alt={studentProfile.studentName || 'Student'}
                              sx={{
                                width: 150,
                                height: 150,
                                margin: '0 auto',
                                border: '5px solid white',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                                bgcolor: !studentProfile.studentImageUrl ? '#667eea' : 'transparent',
                                color: !studentProfile.studentImageUrl ? 'white' : 'inherit',
                                fontSize: !studentProfile.studentImageUrl ? '4rem' : 'inherit',
                                fontWeight: !studentProfile.studentImageUrl ? 700 : 'inherit',
                                transition: 'transform 0.3s ease',
                                '&:hover': { transform: 'scale(1.05)' }
                              }}
                            >
                              {!studentProfile.studentImageUrl && (studentProfile.studentName || 'S').charAt(0)}
                            </Avatar>
                          </Zoom>
                          <Typography variant="h5" sx={{ mt: 2, fontWeight: 700 }}>
                           {studentProfile.studentName || 'N/A'}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255,255,255,0.8)' }}>
                            Email: {studentProfile.emailId || 'N/A'}
                          </Typography>
                          <Chip
                            label={`Roll No: ${studentProfile.rollNo}`}
                            sx={{
                              mt: 1,
                              bgcolor: 'rgba(255,255,255,0.2)',
                              color: 'white',
                              fontWeight: 600,
                              animation: 'pulse 2s ease-in-out infinite',
                              '@keyframes pulse': {
                                '0%, 100%': { transform: 'scale(1)' },
                                '50%': { transform: 'scale(1.05)' }
                              }
                            }}
                          />
                        </>
                      );
                    })()}
                  </Grid>
                  <Grid item xs={12} md={9}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        {[
                          { icon: School, label: 'Class & Section', value: `${studentProfile.className} - ${studentProfile.sectionName}` },
                          { icon: CalendarToday, label: 'Admission Date', value: studentProfile.admissionDate }
                        ].map((item, i) => (
                          <Box
                            key={i}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mb: 2,
                              animation: `slideInLeft 0.8s ease-out ${i * 0.1}s both`,
                              '@keyframes slideInLeft': {
                                '0%': { transform: 'translateX(-30px)', opacity: 0 },
                                '100%': { transform: 'translateX(0)', opacity: 1 }
                              }
                            }}
                          >
                            <item.icon sx={{ mr: 2, fontSize: 28 }} />
                            <Box>
                              <Typography variant="caption" sx={{ opacity: 0.9 }}>{item.label}</Typography>
                              <Typography variant={i === 0 ? "h6" : "body1"} sx={{ fontWeight: i === 0 ? 600 : 500 }}>
                                {item.value}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        {[
                          { label: "Father's Name", value: studentProfile.fathersName || 'N/A' },
                          { label: "Mother's Name", value: studentProfile.mothersName || 'N/A' },
                          { icon: Phone, label: 'Contact', value: studentProfile.fathersMobileNo || 'N/A' }
                        ].map((item, i) => (
                          <Box
                            key={i}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              mb: 2,
                              animation: `slideInRight 0.8s ease-out ${i * 0.1}s both`,
                              '@keyframes slideInRight': {
                                '0%': { transform: 'translateX(30px)', opacity: 0 },
                                '100%': { transform: 'translateX(0)', opacity: 1 }
                              }
                            }}
                          >
                            {item.icon ? <item.icon sx={{ mr: 2, fontSize: 28 }} /> : <Person sx={{ mr: 2, fontSize: 28 }} />}
                            <Box>
                              <Typography variant="caption" sx={{ opacity: 0.9 }}>{item.label}</Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>{item.value}</Typography>
                            </Box>
                          </Box>
                        ))}
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="body2" sx={{ opacity: 0.9, mr: 2 }}>
                            Admission No: {studentProfile.admissionNo}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            DOB: {studentProfile.studentDOB}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grow>
        )}

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            { icon: CheckCircle, value: summary.present, label: 'Present Days', gradient: 'linear-gradient(135deg, #6e8efb 0%, #a777e3 100%)', delay: 0 },
            { icon: Cancel, value: summary.absent, label: 'Absent Days', gradient: 'linear-gradient(135deg, #f66d9b 0%, #df3d82 100%)', delay: 100 },
            { icon: EventBusy, value: summary.holidays, label: 'Holidays', gradient: 'linear-gradient(135deg, #4facfe 0%, #00d2ff 100%)', delay: 200 },
            { icon: TrendingUp, value: `${summary.attendancePercent}%`, label: 'Attendance', gradient: 'linear-gradient(135deg, #4cd964 0%, #5ac8fa 100%)', delay: 300 }
          ].map((stat, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Grow in={true} timeout={1000} style={{ transformOrigin: '0 0 0' }}>
                <Card sx={{
                  borderRadius: 4,
                  boxShadow: '0 6px 24px rgba(0, 0, 0, 0.08)',
                  background: stat.gradient,
                  color: 'white',
                  height: '100%',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  animation: `floatIn 0.8s ease-out ${stat.delay}ms both`,
                  '&:hover': {
                    transform: 'translateY(-8px) scale(1.02)',
                    boxShadow: '0 12px 32px rgba(0, 0, 0, 0.16)'
                  },
                  '@keyframes floatIn': {
                    '0%': { transform: 'translateY(30px)', opacity: 0 },
                    '100%': { transform: 'translateY(0)', opacity: 1 }
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <stat.icon sx={{
                        fontSize: 44,
                        mr: 2,
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '50%',
                        p: 1,
                        animation: 'iconBounce 2s ease-in-out infinite',
                        '@keyframes iconBounce': {
                          '0%, 100%': { transform: 'translateY(0)' },
                          '50%': { transform: 'translateY(-5px)' }
                        }
                      }} />
                      <Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, fontSize: '2rem' }}>{stat.value}</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.9rem' }}>{stat.label}</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
          ))}
        </Grid>

        <Fade in={true} timeout={1500}>
          <Card sx={{
            borderRadius: 4,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            animation: 'fadeIn 1s ease-out 0.5s both',
            '@keyframes fadeIn': {
              '0%': { opacity: 0 },
              '100%': { opacity: 1 }
            }
          }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
                flexWrap: 'wrap',
                gap: 2
              }}>
                <Typography variant="h5" sx={{
                  fontWeight: 700,
                  color: '#667eea',
                  animation: 'slideInLeft 0.8s ease-out',
                  '@keyframes slideInLeft': {
                    '0%': { transform: 'translateX(-30px)', opacity: 0 },
                    '100%': { transform: 'translateX(0)', opacity: 1 }
                  }
                }}>
                  📅 Attendance Record
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Academic Year</InputLabel>
                    <Select
                      value={selectedYearId}
                      label="Academic Year"
                      onChange={(e) => setSelectedYearId(Number(e.target.value))}
                    >
                      {yearOptions.map((year) => (
                        <MenuItem key={year.financialYearID} value={year.financialYearID}>
                          {year.finanacialYear}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Month</InputLabel>
                    <Select
                      value={selectedMonth}
                      label="Month"
                      onChange={(e) => setSelectedMonth(e.target.value)}
                    >
                      {["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map(month => (
                        <MenuItem key={month} value={month}>{month}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Overall Attendance</Typography>
                  <Typography variant="body2" color="primary" fontWeight={600}>
                    {summary.attendancePercent}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={parseFloat(summary.attendancePercent)}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    bgcolor: '#e0e0e0',
                    '& .MuiLinearProgress-bar': {
                      background: 'linear-gradient( 110deg,  rgba(75,108,183,1) 11.3%, rgba(24,40,72,1) 99.3% )',
                      borderRadius: 5,
                      animation: 'progressGrow 1.5s ease-out',
                      '@keyframes progressGrow': {
                        '0%': { transform: 'scaleX(0)', transformOrigin: 'left' },
                        '100%': { transform: 'scaleX(1)', transformOrigin: 'left' }
                      }
                    }
                  }}
                />
              </Box>
              <Box sx={{
                overflowX: 'auto',
                animation: 'fadeIn 1s ease-out 0.5s both',
                '@keyframes fadeIn': {
                  '0%': { opacity: 0 },
                  '100%': { opacity: 1 }
                }
              }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                  <thead>
                    <tr style={{
                      background: 'linear-gradient(110deg, rgba(75,108,183,1) 11.3%, rgba(24,40,72,1) 99.3%)',
                      color: 'white'
                    }}>
                      <th style={{ padding: '12px', border: '1px solid #ddd', fontWeight: 600 }}>Month</th>
                      {[...Array(31)].map((_, i) => (
                        <th key={i + 1} style={{ padding: '8px', border: '1px solid #ddd', fontSize: '0.875rem' }}>
                          {i + 1}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map(month => (
                      <tr key={month}>
                        <td style={{
                          padding: '12px',
                          border: '1px solid #ddd',
                          fontWeight: 600,
                          background: '#f5f5f5'
                        }}>
                          {month}
                        </td>
                        {[...Array(31)].map((_, i) => {
                          const day = i + 1;
                          const status = getDayStatus(month, day);
                          return (
                            <td
                              key={day}
                              style={{
                                padding: '8px',
                                border: '1px solid #ddd',
                                textAlign: 'center',
                                background: getStatusColor(status),
                                fontWeight: 500,
                                fontSize: '0.875rem',
                                transition: 'all 0.3s ease',
                                cursor: status ? 'pointer' : 'default'
                              }}
                              onMouseEnter={(e) => {
                                if (status) {
                                  e.currentTarget.style.transform = 'scale(1.2)';
                                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
                                  e.currentTarget.style.zIndex = '10';
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.boxShadow = 'none';
                                e.currentTarget.style.zIndex = '1';
                              }}
                            >
                              {status}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
              <Box sx={{
                mt: 4,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 3,
                justifyContent: 'center',
                animation: 'fadeIn 1s ease-out 0.7s both',
                '@keyframes fadeIn': {
                  '0%': { opacity: 0 },
                  '100%': { opacity: 1 }
                }
              }}>
                <Chip icon={<CheckCircle />} label="Present (P)" sx={{ bgcolor: '#e8f5e9', fontWeight: 500 }} />
                <Chip icon={<Cancel />} label="Absent (A)" sx={{ bgcolor: '#ffebee', fontWeight: 500 }} />
                <Chip icon={<EventBusy />} label="Holiday (H)" sx={{ bgcolor: '#e3f2fd', fontWeight: 500 }} />
                <Chip icon={<Weekend />} label="Weekend (W)" sx={{ bgcolor: '#fff9c4', fontWeight: 500 }} />
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Container>
    </Box>
  );
};

export default ParentAttendance;
